import { LocaleString, LocaleStringSet, OmniaAppId } from "../shared/types"
import { Features } from "./types"
import { LayerTypeId, OmniaModuleId, RegionId } from "../shared/generated/enums"
import { HelpLink } from "../shared/shell"
import { RoleId } from "../shared/user"
import { SoilUnit } from "../shared/soil"
import { param } from "../shared/generated/labels"
import { except } from "../shared/utils/array"

export function getInterra(production: boolean): Features {

    return {

        appId: OmniaAppId.Interra,

        brand: {
            name: "Interra Scan",
            images: "interra",
            logoDark: "/images/brand/interra/tagline-dark.svg",
            logoLight: "/images/brand/interra/tagline.svg",
            icon: "/images/brand/interra/icon.svg",

            theme: "interra",

            terramap: "Interra Scan"


        },

        login: "default",
        tcs: false,
        sso: false,
        holdingUserPermissions: false,

        usersnapKey: (production ? "a9479f9e-01b3-4fcf-9cea-a8902f079f80" : "df5bed1a-ad80-4e2f-8a5c-5963545b1e53"),

        ui: {

            mainMenu: {
                collapsible: true,
                groupDataModules: false
            },

        },

        SRID: 4326,

        i18n: true,

        regions: except(
            param.RegionId().map(r => r.value),
            [
                RegionId.England,
                RegionId.Scotland,
                RegionId.Wales
            ]
        ),

        account: {
            tcs: false,
            sso: false,
            resetPasswordBeforeLogin: true
        },

        support: {
            phone: undefined,
            email: undefined,
            getHelpLinks: getHelpLinks
        },

        roleNames: {
            [RoleId.Access]: "Basic",
            [RoleId.Client]: "Advanced",
            [RoleId.Agronomist]: "Advisor",
            [RoleId.Admin]: "Admin",
            [RoleId.Master]: "Master"
        } as LocaleStringSet,

        features: {

            holding: {
                timezones: true,
                postTown: false,
                weather: false,
                business: {
                    available: false,
                    ids: []
                }
            },

            fields: {
                cropAttributes: false,
                variety: "free",
                yield: "free",
                destructionDate: false,
                soilTexture: { available: false },
                fieldMargin: {},
                cropManagementOptions: false
            },

            nvz: false,
            facts: false,
            soilMgmtPlans: false,

            soil: {
                module: false,
                morgan: false,
                units: [
                    { id: SoilUnit.Ppm, label: "ppm" },
                    { id: SoilUnit.Mg100g }
                ]
            },

            seeds: false,
            nutrition: false,
            genericPlans: false,
            sprayPlans: false,
            prescriptionPlans: true,

            manure: false,

            stocks: {
                seeds: true,
                ferts: true,
                manure: false,
                costs: false,
                v2: false,
            },

            diary: {
                enabled: false,
            },

            climate: false,
            planning: false,

            maps: {
                geolocationQuerySuffix: undefined,
                basemaps: {
                    ordnanceSurvey: false
                },
            },

            layers: {
                holding: false,
                types: getSupportedLayerTypes()
            },

            satelliteImagery: false,

            import: {

                sources: {
                    documents: false,
                    gatekeeper: false,
                    glgm: false,
                    plantVision: false,
                    soilAnalysis: false,
                    soyl: false,
                    rpa: false,
                    rhiza: false,
                    johnDeere: {
                        enableOperations: false
                    }
                },


                holding: {
                    title: "Holding" as LocaleString,
                    icon: "fas fa-home",
                    img: "/images/brand/interra/icon.svg",
                    text: "Import a complete holding" as LocaleString
                },

                terramap: {
                    title: "Interra Scan Results" as LocaleString,
                    imgIcon: "/images/brand/interra/icon.svg",
                    img: "/images/brand/interra/tagline.svg",
                    imgStyle: { width: "200px" },
                },

                soiloptix: {
                    sharepointFileProvider: false
                }

            },

            export: {

                enabled: {
                    connect: false,
                    glgm: false,
                    soyl: false,
                    yagro: false
                },

                holding: {
                    img: "/images/brand/interra/icon.svg",
                    imgStyle: { width: "100px" }
                },

                layers: {
                    gatekeeperShp: false
                }

            },

            reports: {
                email: false,
            },

            connections: {
                dropbox: true,
                johnDeere: true,
                claas: false,
                fieldView: false,
                glgm: false,
                connect: false,
            },

        }


    }

}

function getSupportedLayerTypes() {

    const all = param.LayerTypeId().map(t => t.value)

    return except(all, [
        LayerTypeId.SubsoilType,
        LayerTypeId.Biomass,
        LayerTypeId.EcScan,
        LayerTypeId.YieldFieldPerformance,
        LayerTypeId.Drawing,
        LayerTypeId.PcnAnalysis,
        LayerTypeId.SlugPressure,
        LayerTypeId.WeedPressure,
        LayerTypeId.SeedbedCondition,
        LayerTypeId.EstablishmentPct,
        LayerTypeId.GenericPlan,
        LayerTypeId.SoilAnalysis,
        LayerTypeId.GenericPlan,
        LayerTypeId.Picture
    ])
}


function getHelpLinks(): Record<string | number, HelpLink[]> {

    return {

        [OmniaModuleId.Dashboard]: [

            { title: 'Platform Overview', link: 'VxNuEJ4cCmM' },
            { title: 'Creating a Holding', link: 'NRztevSK7Yg' },
            { title: 'Connecting to Dropbox', link: 'GA8mhSevyZ8' },
            { title: 'Connecting to MyJohnDeere', link: 'cPK-AT-aZmo' },

        ],

        [OmniaModuleId.Fields]: [

            { title: 'Creating a Field and Field Boundary', link: 'dIqTusxT1Z4' },
            { title: 'Adding a Cropping', link: 'JUOHVfdHzpQ' },
            { title: 'Changing a Cropping', link: 'W93BumVuc9I' },
            { title: 'Importing Field Boundaries From Files', link: 'FHe03iHJYvM' },
            { title: 'Editing a Field Boundary', link: 'FxUiB2FN_mw' },
            { title: 'Fields Module Overview', link: 'W26Jw8mGS2E' },

        ],

        [OmniaModuleId.Layers]: [

            { title: 'Viewing InterraScan Layers', link: 'AQt8dV0kStY' },
            { title: 'Creating a Map Layer', link: 'i570pcpMnBM' },
            { title: 'Editing a Map Layer', link: 'NTSUw74nkkQ' },
            { title: 'Creating Product Plans', link: 'RQTceCu76ho' },

        ],

        [OmniaModuleId.Imports]: [
            { title: 'Importing Raw SoilOptix Data and Soil Lab Results', link: 'b9iBDL-mztM' },
            { title: 'Importing SoilOptix Results', link: 'zLEVc_f18SI' },
        ],

        [OmniaModuleId.Exports]: [
            { title: 'Exporting a Product Plan', link: 'V6FIIVZHA1k' },
        ],

        [OmniaModuleId.Reports]: [
            { title: 'Reports Module Overview', link: 'kG2c_3ujApE' },
        ],

        [OmniaModuleId.Reports2]: [
            { title: 'Reports2 Module Overview', link: 'kG2c_3ujApE' },
        ],

        [OmniaModuleId.Prescriptions]: [
            { title: 'Creating a prescription template', link: 'UcMzwLXjoXk' },
            { title: 'Creating a prescription plan', link: 'Kal_ona9-9U' },
            { 'title': 'Sharing a prescription template', 'link': 'L9tpvamBwnA' },
            { 'title': 'Modifying a prescription plan', 'link': 'QihPjJ8G2k0' },
            { 'title': 'Exporting a prescription plan', 'link': 'swm3YSvOS_4' },]

    }

}
